<template>
  <div class="px-24 md:px-160">
    <RevBreadcrumbs :ariaLabel="i18n(translations.breadcrumb)" :breads />
    <div class="mt-32 w-full md:mt-72 md:w-1/2">
      <h1 class="heading-1 mb-16">{{ i18n(translations.title) }}</h1>
      <RevInfoBlock
        class="mb-24 mt-12 md:mt-4"
        :content="i18n(translations.infoBlockDescription)"
        :icon="IconWarning"
        :title="i18n(translations.infoBlockTitle)"
        variant="warning"
      />
      <RevCard>
        <div v-if="status === 'pending'">
          <SelectYourOrderlineOrderItemSkeleton v-for="item in 2" :key="item" />
        </div>
        <div v-if="orderItems?.results">
          <SelectYourOrderlineOrderItem
            v-for="(orderItem, idx) in orderItems.results"
            :key="idx"
            :action="orderItem.action"
            :has-separator="idx < orderItems.results.length - 1"
            :image="orderItem.image"
            :subtitle="orderItem.subtitle"
            :title="orderItem.title"
          />
        </div>
      </RevCard>
    </div>
  </div>
  <Intercom data-test="intercom" :manual="true" />
</template>

<script setup lang="ts">
import {
  createError,
  onBeforeRouteUpdate,
  useAsyncData,
  useRoute,
  useRouteLocationWithLocale,
} from '#imports'

import Intercom from '@backmarket/nuxt-layer-care-platform/intercom/Intercom.vue'
import { useIntercom } from '@backmarket/nuxt-layer-care-platform/intercom/useIntercom'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevBreadcrumbs } from '@ds/components/Breadcrumbs'
import type { Bread } from '@ds/components/Breadcrumbs/Breadcrumbs.vue'
import { RevCard } from '@ds/components/Card'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconWarning } from '@ds/icons/IconWarning'

import { ROUTE_NAMES } from '@/scopes/dashboard/constants'

import translations from './SelectYourOrderline.translations'
import SelectYourOrderlineOrderItem from './components/SelectYourOrderlineOrderItem.vue'
import SelectYourOrderlineOrderItemSkeleton from './components/SelectYourOrderlineOrderItemSkeleton.vue'

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const route = useRoute()
const { load, intercom, status: intercomStatus } = useIntercom()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const getLinkedOrderItemsKey = getAsyncDataKey('linked-order-items-data', {
  orderlinePublicId: route.params.orderlinePublicId,
  initialAction: route.query.initialAction,
})
const { DASHBOARD } = ROUTE_NAMES
const breads: Bread[] = [
  {
    link: resolveLocalizedRoute({ name: DASHBOARD.MY_ORDERS.SELF }),
    title: i18n(translations.myOrders),
  },
  {
    title: i18n(translations.title),
    link: resolveLocalizedRoute({
      name: DASHBOARD.MY_ORDERS.SELECT_YOUR_ORDERLINE,
    }),
  },
]
const {
  data: orderItems,
  error,
  status,
} = useAsyncData(getLinkedOrderItemsKey, () =>
  $httpFetch('/my-orders/orderlines/:orderlinePublicId/linked-order-items', {
    pathParams: {
      orderlinePublicId: route.params.orderlinePublicId as string,
    },
    query: {
      initialAction: route.query.initialAction as string,
    },
  }),
)

if (error.value) {
  throw createError({
    statusCode: error.value.statusCode,
    statusMessage: error.value.message,
    fatal: true,
  })
}

onBeforeRouteUpdate(async (to) => {
  if (to.query.intercom_ticket_id) {
    if (intercomStatus.value !== 'loaded') await load()
    intercom('showConversation', to.query.intercom_ticket_id)
  }
})
</script>
